import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import deliveryService, {
  NovaPoshtaCityOption,
  NovaPoshtaStreetOption,
  NovaPoshtaWarehouseOption,
} from '../Rest/Services/DeliveryService/DeliveryService';
import {
  DeliveryDestination,
  DeliveryInformationData,
  NovaPoshtaDeliveryAddressData,
} from '../Rest/Services/UserService/Data';
import './NovaPoshtaAddressFields.css';

interface NovaPoshtaAddressFieldsProps {
  value: DeliveryInformationData;
  onChange: (next: DeliveryInformationData) => void;
  destinationControl?: React.ReactNode;
}

const emptyNovaAddress: NovaPoshtaDeliveryAddressData = {
  cityRef: '',
  cityName: '',
};

const targetLabel: Record<DeliveryDestination, string> = {
  [DeliveryDestination.Department]: 'Відділення Нової пошти',
  [DeliveryDestination.ParcelLocker]: 'Поштомат Нової пошти',
  [DeliveryDestination.Address]: 'Вулиця',
};

function NovaPoshtaAddressFields({ value, onChange, destinationControl }: NovaPoshtaAddressFieldsProps) {
  const [cityInput, setCityInput] = useState(value.city || '');
  const [warehouseInput, setWarehouseInput] = useState(value.novaPoshtaAddress?.warehouseDescription || '');
  const [streetInput, setStreetInput] = useState(value.novaPoshtaAddress?.streetName || '');
  const [cityOptions, setCityOptions] = useState<NovaPoshtaCityOption[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<NovaPoshtaWarehouseOption[]>([]);
  const [streetOptions, setStreetOptions] = useState<NovaPoshtaStreetOption[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const [streetsLoading, setStreetsLoading] = useState(false);
  const [citiesError, setCitiesError] = useState(false);
  const [warehousesError, setWarehousesError] = useState(false);
  const [streetsError, setStreetsError] = useState(false);

  const nova = value.novaPoshtaAddress ?? emptyNovaAddress;
  const isCourierAddress = value.destination === DeliveryDestination.Address;

  const selectedCity = useMemo<NovaPoshtaCityOption | null>(() => {
    if (!nova.cityRef) return null;
    return {
      ref: nova.settlementRef || nova.cityRef,
      deliveryCityRef: nova.cityRef,
      name: nova.cityName || value.city,
      present: buildCityPresent(nova, value.city),
      areaName: nova.areaName,
      regionName: nova.regionName,
      settlementType: nova.settlementType,
      warehouses: 0,
      addressDeliveryAllowed: true,
      streetsAvailability: true,
    };
  }, [nova, value.city]);

  const selectedWarehouse = useMemo<NovaPoshtaWarehouseOption | null>(() => {
    if (!nova.warehouseRef) return null;
    return {
      ref: nova.warehouseRef,
      cityRef: nova.cityRef,
      settlementRef: nova.settlementRef,
      cityName: nova.cityName || value.city,
      number: nova.warehouseNumber || '',
      description: nova.warehouseDescription || value.number,
      shortAddress: nova.warehouseShortAddress,
      typeOfWarehouseRef: nova.warehouseTypeRef,
      categoryOfWarehouse: nova.warehouseCategory,
      postalCode: nova.postalCode,
    };
  }, [nova, value.city, value.number]);

  const selectedStreet = useMemo<NovaPoshtaStreetOption | null>(() => {
    if (!nova.streetName) return null;
    return {
      ref: nova.streetRef || nova.streetName,
      name: nova.streetName,
      type: nova.streetType,
    };
  }, [nova.streetName, nova.streetRef, nova.streetType]);

  useEffect(() => {
    setCityInput(value.city || nova.cityName || '');
  }, [nova.cityRef, nova.cityName, value.city]);

  useEffect(() => {
    setWarehouseInput(nova.warehouseDescription || '');
  }, [nova.warehouseRef, nova.warehouseDescription]);

  useEffect(() => {
    setStreetInput(nova.streetName || '');
  }, [nova.streetRef, nova.streetName]);

  useEffect(() => {
    const query = cityInput.trim();
    if (query.length < 2) {
      setCityOptions([]);
      return;
    }

    let ignore = false;
    setCitiesLoading(true);
    setCitiesError(false);
    const timeoutId = window.setTimeout(() => {
      deliveryService
        .searchNovaPoshtaCities(query)
        .then((items) => {
          if (!ignore) setCityOptions(items);
        })
        .catch(() => {
          if (!ignore) {
            setCityOptions([]);
            setCitiesError(true);
          }
        })
        .finally(() => {
          if (!ignore) setCitiesLoading(false);
        });
    }, 300);

    return () => {
      ignore = true;
      window.clearTimeout(timeoutId);
    };
  }, [cityInput]);

  useEffect(() => {
    if (!nova.cityRef || isCourierAddress) {
      setWarehouseOptions([]);
      return;
    }

    let ignore = false;
    setWarehousesLoading(true);
    setWarehousesError(false);
    const timeoutId = window.setTimeout(() => {
      deliveryService
        .searchNovaPoshtaWarehouses(nova.cityRef, value.destination, warehouseInput)
        .then((items) => {
          if (!ignore) setWarehouseOptions(items);
        })
        .catch(() => {
          if (!ignore) {
            setWarehouseOptions([]);
            setWarehousesError(true);
          }
        })
        .finally(() => {
          if (!ignore) setWarehousesLoading(false);
        });
    }, 300);

    return () => {
      ignore = true;
      window.clearTimeout(timeoutId);
    };
  }, [isCourierAddress, nova.cityRef, value.destination, warehouseInput]);

  useEffect(() => {
    const query = streetInput.trim();
    if (!nova.cityRef || !isCourierAddress || query.length < 2) {
      setStreetOptions([]);
      return;
    }

    let ignore = false;
    setStreetsLoading(true);
    setStreetsError(false);
    const timeoutId = window.setTimeout(() => {
      deliveryService
        .searchNovaPoshtaStreets(nova.cityRef, query)
        .then((items) => {
          if (!ignore) setStreetOptions(items);
        })
        .catch(() => {
          if (!ignore) {
            setStreetOptions([]);
            setStreetsError(true);
          }
        })
        .finally(() => {
          if (!ignore) setStreetsLoading(false);
        });
    }, 300);

    return () => {
      ignore = true;
      window.clearTimeout(timeoutId);
    };
  }, [isCourierAddress, nova.cityRef, streetInput]);

  const handleCityChange = (_: unknown, option: NovaPoshtaCityOption | null) => {
    if (!option) {
      onChange({
        ...value,
        city: '',
        number: '',
        displayName: '',
        regionName: null,
        districtName: null,
        novaPoshtaAddress: null,
      });
      return;
    }

    onChange({
      ...value,
      city: option.name,
      number: '',
      displayName: '',
      regionName: option.areaName ?? null,
      districtName: option.regionName ?? null,
      novaPoshtaAddress: {
        cityRef: option.deliveryCityRef,
        settlementRef: option.ref,
        cityName: option.name,
        areaName: option.areaName ?? null,
        regionName: option.regionName ?? null,
        settlementType: option.settlementType ?? null,
      },
    });
  };

  const handleWarehouseChange = (_: unknown, option: NovaPoshtaWarehouseOption | null) => {
    const currentNova = value.novaPoshtaAddress ?? emptyNovaAddress;
    if (!option) {
      onChange({
        ...value,
        number: '',
        displayName: '',
        novaPoshtaAddress: {
          ...currentNova,
          warehouseRef: null,
          warehouseNumber: null,
          warehouseDescription: null,
          warehouseShortAddress: null,
          warehouseTypeRef: null,
          warehouseCategory: null,
          postalCode: null,
        },
      });
      return;
    }

    const number = option.description;
    onChange({
      ...value,
      number,
      displayName: buildDisplayName(value.city, number),
      novaPoshtaAddress: {
        ...currentNova,
        cityRef: currentNova.cityRef || option.cityRef,
        settlementRef: currentNova.settlementRef || option.settlementRef,
        cityName: currentNova.cityName || option.cityName,
        warehouseRef: option.ref,
        warehouseNumber: option.number,
        warehouseDescription: option.description,
        warehouseShortAddress: option.shortAddress ?? null,
        warehouseTypeRef: option.typeOfWarehouseRef ?? null,
        warehouseCategory: option.categoryOfWarehouse ?? null,
        postalCode: option.postalCode ?? null,
      },
    });
  };

  const handleStreetChange = (_: unknown, option: NovaPoshtaStreetOption | null) => {
    const currentNova = value.novaPoshtaAddress ?? emptyNovaAddress;
    const nextNova = {
      ...currentNova,
      streetRef: option?.ref ?? null,
      streetName: option?.name ?? '',
      streetType: option?.type ?? null,
    };

    onChange(buildCourierDelivery(value, nextNova));
  };

  const updateCourierPart = (field: 'buildingNumber' | 'flat', nextValue: string) => {
    const nextNova = {
      ...(value.novaPoshtaAddress ?? emptyNovaAddress),
      [field]: nextValue,
    };

    onChange(buildCourierDelivery(value, nextNova));
  };

  return (
    <div className="delivery-provider-fields">
      <Autocomplete
        options={mergeOption(selectedCity, cityOptions)}
        value={selectedCity}
        inputValue={cityInput}
        loading={citiesLoading}
        onInputChange={(_, nextInput) => setCityInput(nextInput)}
        onChange={handleCityChange}
        isOptionEqualToValue={(option, selected) => option.deliveryCityRef === selected.deliveryCityRef}
        getOptionLabel={(option) => option.present || option.name}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Місто Нової пошти"
            required
            error={citiesError}
            helperText={citiesError ? 'Не вдалося завантажити список міст. Спробуйте ще раз.' : undefined}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {citiesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {destinationControl}

      {!isCourierAddress && (
        <Autocomplete
          options={mergeOption(selectedWarehouse, warehouseOptions)}
          value={selectedWarehouse}
          inputValue={warehouseInput}
          loading={warehousesLoading}
          onInputChange={(_, nextInput) => setWarehouseInput(nextInput)}
          onChange={handleWarehouseChange}
          disabled={!nova.cityRef}
          isOptionEqualToValue={(option, selected) => option.ref === selected.ref}
          getOptionLabel={(option) => option.description}
          renderInput={(params) => (
            <TextField
              {...params}
              label={targetLabel[value.destination]}
              required
              error={warehousesError}
              helperText={warehousesError ? 'Не вдалося завантажити список відділень. Спробуйте ще раз.' : undefined}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {warehousesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}

      {isCourierAddress && (
        <>
          <Autocomplete
            options={mergeOption(selectedStreet, streetOptions)}
            value={selectedStreet}
            inputValue={streetInput}
            loading={streetsLoading}
            onInputChange={(_, nextInput) => setStreetInput(nextInput)}
            onChange={handleStreetChange}
            disabled={!nova.cityRef}
            isOptionEqualToValue={(option, selected) => option.ref === selected.ref}
            getOptionLabel={(option) => option.type ? `${option.type} ${option.name}` : option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Вулиця"
                required
                error={streetsError}
                helperText={streetsError ? 'Не вдалося завантажити список вулиць. Спробуйте ще раз.' : undefined}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {streetsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            label="Будинок"
            value={nova.buildingNumber || ''}
            onChange={(event) => updateCourierPart('buildingNumber', event.target.value)}
            required
          />
          <TextField
            label="Квартира"
            value={nova.flat || ''}
            onChange={(event) => updateCourierPart('flat', event.target.value)}
          />
        </>
      )}
    </div>
  );
}

function mergeOption<T extends { ref: string }>(selected: T | null, options: T[]) {
  if (!selected || options.some((option) => option.ref === selected.ref)) {
    return options;
  }

  return [selected, ...options];
}

function buildCityPresent(address: NovaPoshtaDeliveryAddressData, fallbackCity: string) {
  const city = address.cityName || fallbackCity;
  const details = [address.regionName, address.areaName].filter(Boolean).join(', ');
  return details ? `${city}, ${details}` : city;
}

function buildDisplayName(city: string, target: string) {
  return target ? `${city}, ${target}` : city;
}

function buildCourierDelivery(
  value: DeliveryInformationData,
  nova: NovaPoshtaDeliveryAddressData
): DeliveryInformationData {
  const target = buildCourierTarget(nova);
  return {
    ...value,
    number: target,
    displayName: buildDisplayName(value.city, target),
    novaPoshtaAddress: nova,
  };
}

function buildCourierTarget(nova: NovaPoshtaDeliveryAddressData) {
  const street = [nova.streetType, nova.streetName].filter(Boolean).join(' ').trim();
  const parts = [street];

  if (nova.buildingNumber?.trim()) {
    parts.push(`буд. ${nova.buildingNumber.trim()}`);
  }

  if (nova.flat?.trim()) {
    parts.push(`кв. ${nova.flat.trim()}`);
  }

  return parts.filter(Boolean).join(', ');
}

export default NovaPoshtaAddressFields;
